import { useCallback, useEffect, useState } from 'react'
import { convertDurationToString } from './helpers'

export const useAudioPlayer = (
  ref: React.RefObject<HTMLAudioElement>,
  active: boolean,
  source: string
) => {
  const [playing, setPlaying] = useState(false)
  const [loaded, setLoaded] = useState(false)
  const [unsupported, setUnsupported] = useState(false)
  const [startingDuration, setStartingDuration] = useState('')
  const [currentDuration, setCurrentDuration] = useState('')
  const [progress, setProgress] = useState(0)

  const play = useCallback(() => {
    if (!ref.current) return

    ref.current.play()
    setPlaying(true)
  }, [ref])

  const pause = useCallback(() => {
    if (!ref.current) return

    ref.current.pause()
    setPlaying(false)
  }, [ref])

  // set the audio source
  useEffect(() => {
    if (!ref.current) return

    if (active) {
      ref.current.src = source
      ref.current.load()
    } else {
      ref.current.removeAttribute('src')
      pause()
    }
  }, [ref, source, active, pause])

  // on load
  useEffect(() => {
    if (!ref.current) return

    if (active) {
      const player = ref.current

      const onPlayerLoad = () => {
        const duration = convertDurationToString(player.duration)
        setStartingDuration(duration)
        setCurrentDuration('')
        setProgress(0)
        setPlaying(playing)
        setLoaded(true)
      }

      player.addEventListener('canplaythrough', onPlayerLoad)

      return () => player.removeEventListener('canplaythrough', onPlayerLoad)
    }
  }, [ref, active, playing])

  // on error
  useEffect(() => {
    if (!ref.current) return

    const player = ref.current
    const onPlayerError = () => setUnsupported(true)
    player.addEventListener('error', onPlayerError)
    return () => player.removeEventListener('error', onPlayerError)
  }, [ref])

  // get current audio duration
  useEffect(() => {
    if (!ref.current) return

    if (active) {
      const player = ref.current
      const onPlayerUpdate = () => {
        const duration = player.duration
        const currentTime = player.currentTime
        const currentProgress = Math.min(
          (10 / duration) * currentTime * 10,
          100
        )
        const remainingTime = duration - currentTime
        const durationStr = convertDurationToString(remainingTime)

        if (remainingTime !== 0) {
          setCurrentDuration(durationStr)
          setProgress(currentProgress)
        } else {
          pause()
          setCurrentDuration('')
          setProgress(0)
        }
      }

      player.addEventListener('timeupdate', onPlayerUpdate)

      return () => {
        player.removeEventListener('timeupdate', onPlayerUpdate)
      }
    }
  }, [ref, active, startingDuration, pause])

  return {
    playing,
    loaded,
    unsupported,
    startingDuration,
    currentDuration,
    progress,
    play,
    pause,
  }
}
